import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";

interface CustomDatePickerProps {
  value: string; // ISO format "YYYY-MM-DD"
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAYS_OF_WEEK = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  placeholder = "Select date",
  className = "",
  style = {},
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, width: 300 });

  const parsedValueDate = value ? new Date(value + "T00:00:00") : null;
  const initialDisplayDate = parsedValueDate && !isNaN(parsedValueDate.getTime()) ? parsedValueDate : new Date();

  const [currentYear, setCurrentYear] = useState(initialDisplayDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(initialDisplayDate.getMonth());

  useEffect(() => {
    if (value) {
      const d = new Date(value + "T00:00:00");
      if (!isNaN(d.getTime())) {
        setCurrentYear(d.getFullYear());
        setCurrentMonth(d.getMonth());
      }
    }
  }, [value]);

  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popoverWidth = Math.min(300, window.innerWidth - 32);
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth - 16) {
        left = Math.max(16, window.innerWidth - popoverWidth - 16);
      }
      setPopoverPos({
        top: rect.bottom + window.scrollY + 6,
        left: left + window.scrollX,
        width: popoverWidth,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        const popoverEl = document.getElementById("v3-datepicker-popover-portal");
        if (popoverEl && popoverEl.contains(e.target as Node)) return;
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handleSelectDay = (day: number) => {
    const monthStr = String(currentMonth + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    const formatted = `${currentYear}-${monthStr}-${dayStr}`;
    onChange(formatted);
    setIsOpen(false);
  };

  const handleShortcut = (type: "today" | "tomorrow" | "nextWeek" | "nextMonth") => {
    const target = new Date();
    if (type === "tomorrow") target.setDate(target.getDate() + 1);
    if (type === "nextWeek") target.setDate(target.getDate() + 7);
    if (type === "nextMonth") target.setMonth(target.getMonth() + 1);

    const year = target.getFullYear();
    const monthStr = String(target.getMonth() + 1).padStart(2, "0");
    const dayStr = String(target.getDate()).padStart(2, "0");
    onChange(`${year}-${monthStr}-${dayStr}`);
    setIsOpen(false);
  };

  const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  const formattedDisplay = parsedValueDate && !isNaN(parsedValueDate.getTime())
    ? `${MONTH_NAMES[parsedValueDate.getMonth()].slice(0, 3)} ${parsedValueDate.getDate()}, ${parsedValueDate.getFullYear()}`
    : "";

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", ...style }}>
      <style>{`
        .v3-popover-card {
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: 0 20px 48px rgba(0, 0, 0, 0.15) !important;
        }
        html.night-mode .v3-popover-card,
        .night-mode .v3-popover-card {
          background: rgba(18, 18, 28, 0.98) !important;
          border: 1px solid rgba(255, 255, 255, 0.12) !important;
          box-shadow: 0 20px 48px rgba(0, 0, 0, 0.7) !important;
        }
      `}</style>

      {/* Trigger Input Box */}
      <div
        className={`v3-input ${className}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CalendarIcon size={15} color="#EE3F7C" />
          <span style={{ fontSize: 13, color: formattedDisplay ? "var(--v3-text-main)" : "var(--v3-text-faint)" }}>
            {formattedDisplay || placeholder}
          </span>
        </div>
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            style={{
              background: "none",
              border: "none",
              color: 'var(--v3-text-faint)',
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Popover Calendar Dropdown via Portal */}
      {isOpen &&
        createPortal(
          <div
            id="v3-datepicker-popover-portal"
            className="custom-datepicker-popover v3-popover-card"
            style={{
              position: "absolute",
              top: popoverPos.top,
              left: popoverPos.left,
              zIndex: 999999,
              width: popoverPos.width,
              maxWidth: "calc(100vw - 32px)",
              backdropFilter: "blur(24px) saturate(180%)",
              WebkitBackdropFilter: "blur(24px) saturate(180%)",
              borderRadius: 16,
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 14,
              boxSizing: "border-box",
            }}
          >
            {/* Calendar Header Navigation */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--v3-text-main)' }}>
                {MONTH_NAMES[currentMonth]} {currentYear}
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  style={{
                    background: 'var(--v3-bg-subtle)',
                    border: '1px solid var(--v3-border-subtle)',
                    borderRadius: 8,
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: 'var(--v3-text-main)',
                    cursor: "pointer",
                  }}
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  style={{
                    background: 'var(--v3-bg-subtle)',
                    border: '1px solid var(--v3-border-subtle)',
                    borderRadius: 8,
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: 'var(--v3-text-main)',
                    cursor: "pointer",
                  }}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Days of Week */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, textAlign: "center" }}>
              {DAYS_OF_WEEK.map((d) => (
                <span key={d} style={{ fontSize: 10, fontWeight: 600, color: 'var(--v3-text-faint)' }}>
                  {d}
                </span>
              ))}
            </div>

            {/* Grid of Days */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}

              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const monthStr = String(currentMonth + 1).padStart(2, "0");
                const dayStr = String(day).padStart(2, "0");
                const dayDateStr = `${currentYear}-${monthStr}-${dayStr}`;
                const isSelected = value === dayDateStr;

                const today = new Date();
                const isToday =
                  today.getFullYear() === currentYear &&
                  today.getMonth() === currentMonth &&
                  today.getDate() === day;

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleSelectDay(day)}
                    style={{
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      border: isSelected ? "none" : isToday ? "1px solid rgba(238, 63, 124, 0.5)" : "none",
                      background: isSelected
                        ? "linear-gradient(135deg, #EE3F7C 0%, #F79B59 100%)"
                        : isToday
                        ? "rgba(238, 63, 124, 0.12)"
                        : "transparent",
                      color: isSelected ? "#ffffff" : isToday ? "#EE3F7C" : "var(--v3-text-main)",
                      fontSize: 12,
                      fontWeight: isSelected || isToday ? 700 : 500,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      boxShadow: isSelected ? "0 4px 12px rgba(238, 63, 124, 0.3)" : "none",
                    }}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Quick Shortcuts */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => handleShortcut("today")}
                style={{
                  flex: 1,
                  padding: "5px 8px",
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--v3-text-muted)',
                  background: 'var(--v3-bg-subtle)',
                  border: '1px solid var(--v3-border-subtle)',
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => handleShortcut("tomorrow")}
                style={{
                  flex: 1,
                  padding: "5px 8px",
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--v3-text-muted)',
                  background: 'var(--v3-bg-subtle)',
                  border: '1px solid var(--v3-border-subtle)',
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Tomorrow
              </button>
              <button
                type="button"
                onClick={() => handleShortcut("nextWeek")}
                style={{
                  flex: 1,
                  padding: "5px 8px",
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--v3-text-muted)',
                  background: 'var(--v3-bg-subtle)',
                  border: '1px solid var(--v3-border-subtle)',
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                +1 Week
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomDatePicker;
